import React, { useEffect, useRef } from 'react'
import { get_color, safe_get } from "../../report-visuals/report-utils";
import _ from "lodash";
import { observer } from "mobx-react-lite";
import { importAmCharts5Core, importAmCharts5XY, importAmCharts5Theme } from "../../libs/amcharts5/imports";

export const BarChart = observer((props: any) => {

    const { data, _label, value, id = "barchart" } = props
    const chartRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (typeof window === "undefined" || !data || !containerRef.current) return;

        let chart_data = data;
        const colorsList = chart_data.map((x: any, ind: number) => {
            return get_color(ind);
        });

        chart_data = _.orderBy(chart_data, value, "desc");

        const initChart = async () => {
            try {
                // Lazy load AmCharts 5 modules
                await importAmCharts5Core();
                await importAmCharts5XY();
                await importAmCharts5Theme("Animated");

                const am5 = (window as any).am5;
                const am5xy = (window as any).am5xy;
                const AnimatedTheme = (window as any).am5themes_Animated;

                if (!am5 || !am5xy) {
                    console.warn("amCharts5 not loaded");
                    return;
                }

                // Dispose previous chart if exists
                if (chartRef.current) {
                    chartRef.current.dispose();
                }

                // Create root element
                const root = am5.Root.new(containerRef.current);
                if (AnimatedTheme) {
                    root.setThemes([AnimatedTheme.new(root)]);
                }

                // Create chart
                const chart = root.container.children.push(
                    am5xy.XYChart.new(root, {
                        panX: false,
                        panY: false,
                        wheelX: "none",
                        wheelY: "none",
                    })
                );

                // Category X-axis
                const categoryAxis = chart.xAxes.push(
                    am5xy.CategoryAxis.new(root, {
                        categoryField: "label",
                        renderer: am5xy.AxisRendererX.new(root, {
                            minGridDistance: 10,
                        }),
                    })
                );

                categoryAxis.get("renderer").labels.template.setAll({
                    rotation: -65,
                    dx: -50,
                    dy: -10,
                    fontSize: 12,
                    fill: am5.color("#4b5563"),
                });

                categoryAxis.get("renderer").grid.template.setAll({
                    visible: true,
                    stroke: am5.color("#e5e7eb"),
                    strokeOpacity: 0.2,
                });

                // Value Y-axis
                const valueAxis = chart.yAxes.push(
                    am5xy.ValueAxis.new(root, {
                        renderer: am5xy.AxisRendererY.new(root, {}),
                    })
                );

                // Disable labels
                categoryAxis.get("renderer").labels.template.setAll({ visible: false });
                valueAxis.get("renderer").labels.template.setAll({ visible: false });

                // Create series
                const series = chart.series.push(
                    am5xy.ColumnSeries.new(root, {
                        name: "Voters %",
                        xAxis: categoryAxis,
                        yAxis: valueAxis,
                        valueYField: "value",
                        categoryXField: "label",
                    })
                );

                // Configure columns
                series.columns.template.setAll({
                    width: am5.percent(50),
                    fill: am5.color("#fff"),
                    tooltipText: "{categoryX}: [bold]{valueY}[/]",
                });

                // Add colors from colorsList
                series.columns.template.adapters.add("fill", (fill, target, index) => {
                    if (colorsList[index]) {
                        return am5.color(colorsList[index]);
                    }
                    return fill;
                });

                // Set data
                series.data.setAll(chart_data);
                categoryAxis.data.setAll(chart_data);

                // Add cursor
                chart.set("cursor", am5xy.XYCursor.new(root, {
                    behavior: "none",
                }));

                // Animate
                series.appear(1000);
                chart.appear(1000, 100);

                chartRef.current = root;
            } catch (error) {
                console.error("Error initializing AmCharts 5 chart:", error);
            }
        };

        initChart();

        return () => {
            if (chartRef.current) {
                chartRef.current.dispose();
                chartRef.current = null;
            }
        };
    }, [data, value]);

    return (
        <div style={{ "width": "100%", "height": "600px" }} className={"flex flex-col mx-auto"}>
            <div ref={containerRef} id={id} className='w-full h-full' />
        </div>
    )
})